import { useCallback, useEffect, useMemo, useState } from 'react';
import financeService from '../../../services/financeService';
import { ACCOUNT_TYPE_LABEL, ChartResponse, LedgerAccount } from '../../../types/finance';
import { SelectOption } from '../../common/SearchableSelect';

/** The school's chart with balances, plus ready-made pickers over it. */
export const useChart = (enabled = true) => {
  const [chart, setChart] = useState<ChartResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setChart(await financeService.getChart());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the chart of accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) reload(); }, [reload, enabled]);

  const accounts = useMemo(() => chart?.accounts ?? [], [chart]);
  const options = useMemo(() => {
    const toOption = (a: LedgerAccount): SelectOption => ({
      value: a._id, label: `${a.code} · ${a.name}`, secondary: ACCOUNT_TYPE_LABEL[a.type],
    });
    // Settings store account codes (stable across schools), not ids.
    const byCode = (a: LedgerAccount): SelectOption => ({ ...toOption(a), value: a.code });
    return {
      /** Accounts a person may post a manual journal to. */
      postable: accounts.filter(a => !a.isGroup && a.active && !a.controlModule).map(toOption),
      /** Every account, groups included — a group's ledger covers the accounts beneath it. */
      all: accounts.map(a => ({ ...toOption(a), secondary: a.isGroup ? `${ACCOUNT_TYPE_LABEL[a.type]} · group` : ACCOUNT_TYPE_LABEL[a.type] })),
      /** Every account that can carry lines — for filtering. */
      ledger: accounts.filter(a => !a.isGroup).map(toOption),
      /** Where each kind of category may post (the server's rule). */
      feeHeads: accounts.filter(a => a.usableFor.includes('fee')).map(byCode),
      expenseHeads: accounts.filter(a => a.usableFor.includes('expense')).map(byCode),
      incomeHeads: accounts.filter(a => a.usableFor.includes('income')).map(byCode),
      /** Where payments may land: cash and bank accounts. */
      cashAndBank: accounts.filter(a => a.usableFor.includes('payment')).map(byCode),
    };
  }, [accounts]);

  return { chart, accounts, options, loading, error, reload };
};
